<template>
  <div class="flyover-profile-bar">
    <!-- Stats row: a single horizontal line of equal-width cells directly above the profile (not a
         box), matching the iOS/Android flyover. Fixed-width cells so the live values never reflow. -->
    <div class="flyover-stats">
      <div class="flyover-stat">
        <div class="flyover-stat-value">{{ distanceLabel }}</div>
        <div class="flyover-stat-label">Distance</div>
      </div>
      <div class="flyover-stat">
        <div class="flyover-stat-value">{{ elevationLabel }}</div>
        <div class="flyover-stat-label">Elevation</div>
      </div>
      <div class="flyover-stat">
        <div class="flyover-stat-value">{{ gradeLabel }}</div>
        <div class="flyover-stat-label">Grade</div>
      </div>
    </div>

    <div v-if="hasProfile" class="flyover-profile-wrap">
      <svg
        class="flyover-profile-svg"
        :viewBox="`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`"
        preserveAspectRatio="none"
      >
        <defs>
          <clipPath :id="clipId">
            <rect x="0" y="0" :width="revealWidth" :height="VIEWBOX_H" />
          </clipPath>
          <linearGradient :id="gradId" x1="0" y1="0" x2="1" y2="0">
            <stop
              v-for="(s, i) in gradientStops"
              :key="i"
              :offset="s.offset"
              :stop-color="s.color"
            />
          </linearGradient>
          <!-- vertical fade: solid near the ridge line, transparent toward the baseline -->
          <linearGradient :id="fadeId" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#fff" stop-opacity="1" />
            <stop offset="0.65" stop-color="#fff" stop-opacity="0.4" />
            <stop offset="1" stop-color="#fff" stop-opacity="0" />
          </linearGradient>
          <mask :id="maskId">
            <rect x="0" y="0" :width="VIEWBOX_W" :height="VIEWBOX_H" :fill="`url(#${fadeId})`" />
          </mask>
        </defs>

        <!-- horizontal gridlines -->
        <line
          v-for="gy in [28, 64, 100]"
          :key="`grid-${gy}`"
          x1="0"
          :y1="gy"
          :x2="VIEWBOX_W"
          :y2="gy"
          stroke="rgba(255,255,255,.1)"
          stroke-width="1"
          stroke-dasharray="3 7"
          vector-effect="non-scaling-stroke"
        />

        <!-- steepness-coloured fill, revealed with progress, fading toward the baseline -->
        <path
          :d="areaPath"
          :fill="`url(#${gradId})`"
          fill-opacity="0.82"
          :clip-path="`url(#${clipId})`"
          :mask="`url(#${maskId})`"
        />
        <!-- faint full "ahead" ghost line -->
        <path
          :d="linePath"
          fill="none"
          stroke="rgba(255,255,255,0.28)"
          stroke-width="2"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />
        <!-- glow + crisp coloured line, revealed with progress -->
        <path
          :d="linePath"
          fill="none"
          :stroke="`url(#${gradId})`"
          stroke-width="7"
          stroke-opacity="0.35"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
          :clip-path="`url(#${clipId})`"
        />
        <path
          :d="linePath"
          fill="none"
          :stroke="`url(#${gradId})`"
          stroke-width="3.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          vector-effect="non-scaling-stroke"
          :clip-path="`url(#${clipId})`"
        />
      </svg>

      <!-- head dot: an HTML element (not the stretched SVG) so it stays a round dot -->
      <div class="flyover-profile-head" :style="headStyle"></div>
    </div>
  </div>
</template>

<script>
import { getElevationAtDistanceKm } from "../../utils/helpers/trailPreviewMath";
import {
  PROFILE_VIEWBOX_W,
  PROFILE_VIEWBOX_H,
  computeProfile,
  steepColor,
  profileYAt,
  gradeAtDistance,
  formatGrade,
} from "../../utils/helpers/flyoverProfile";

// Per-page counter so each instance's SVG defs (clip/gradient/mask) get unique ids and never collide.
let flyoverProfileUid = 0;

export default {
  name: "FlyoverElevationProfile",
  props: {
    // Flattened route vertices [lng, lat, elev?] for the WHOLE flyover.
    coords: { type: Array, default: () => [] },
    // Indices into `coords` that start a new stroke of the walk — the gaps a hike merged out of
    // several routes has between them. Excluded from the profile's distance, exactly as they are
    // excluded from the flyover's own timeline.
    breaks: { type: Array, default: () => [] },
    // Playback progress 0..1.
    progress: { type: Number, default: 0 },
    // Pre-formatted live stats from the parent (already run through the user's measurement pref).
    distanceLabel: { type: String, default: "—" },
    elevationLabel: { type: String, default: "—" },
  },
  data() {
    // Unique ids so multiple instances (or a leftover from a previous open) never collide on defs.
    const uid = (flyoverProfileUid += 1);
    return {
      VIEWBOX_W: PROFILE_VIEWBOX_W,
      VIEWBOX_H: PROFILE_VIEWBOX_H,
      clipId: `flyoverProfClip-${uid}`,
      gradId: `flyoverProfSteep-${uid}`,
      fadeId: `flyoverProfFade-${uid}`,
      maskId: `flyoverProfMask-${uid}`,
    };
  },
  computed: {
    profile() {
      return computeProfile(this.coords || [], this.breaks || []);
    },
    hasProfile() {
      return this.profile.points.length > 1;
    },
    gradientStops() {
      const { points, cumKm, elevSmooth } = this.profile;
      const pointGrade = (i) => {
        const a = Math.max(0, i - 1);
        const b = Math.min(points.length - 1, i + 1);
        const dh = (cumKm[b] - cumKm[a]) * 1000;
        return dh > 0 ? ((elevSmooth[b] - elevSmooth[a]) / dh) * 100 : 0;
      };
      return points.map((p, i) => ({
        offset: `${((p.x / this.VIEWBOX_W) * 100).toFixed(2)}%`,
        color: steepColor(pointGrade(i)),
      }));
    },
    areaPath() {
      const pts = this.profile.points;
      if (pts.length === 0) return "";
      const body = pts.map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
      return `M0,${this.VIEWBOX_H} ${body} L${this.VIEWBOX_W},${this.VIEWBOX_H} Z`;
    },
    linePath() {
      const pts = this.profile.points;
      if (pts.length === 0) return "";
      return `M${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L")}`;
    },
    clampedProgress() {
      return Math.min(Math.max(this.progress || 0, 0), 1);
    },
    revealWidth() {
      return (this.clampedProgress * this.VIEWBOX_W).toFixed(1);
    },
    headStyle() {
      const y = profileYAt(this.profile.points, this.clampedProgress);
      return {
        left: `${this.clampedProgress * 100}%`,
        top: `${(y / this.VIEWBOX_H) * 100}%`,
      };
    },
    gradeLabel() {
      if (!this.hasProfile) return "—";
      const { totalKm, cumKm } = this.profile;
      const coords3d = this.coords;
      const distKm = this.clampedProgress * totalKm;
      const grade = gradeAtDistance(
        distKm,
        totalKm,
        (d) => getElevationAtDistanceKm(d, coords3d, cumKm)
      );
      return formatGrade(grade);
    },
  },
};
</script>

<style scoped>
.flyover-profile-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  padding: 1.5em 1.5em 1.25em;
  color: #fff;
  pointer-events: none;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0));
}

.flyover-stats {
  display: flex;
  align-items: flex-end;
  gap: 2.5em;
  margin-bottom: 0.5em;
}
.flyover-stat {
  flex: 1 1 0;
  min-width: 0;
}
.flyover-stat-value {
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.flyover-stat-label {
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;
  margin-top: 4px;
  color: #ffd400;
}

.flyover-profile-wrap {
  position: relative;
  width: 100%;
  height: 64px;
}
.flyover-profile-svg {
  display: block;
  width: 100%;
  height: 100%;
}
.flyover-profile-head {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #111;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

@media (max-width: 768px) {
  .flyover-profile-bar {
    padding: 1.25em 1em 1em;
  }
  .flyover-stats {
    gap: 1em;
  }
  .flyover-stat-value {
    font-size: 18px;
  }
  .flyover-profile-wrap {
    height: 52px;
  }
}
</style>
